import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useBrand } from '@/hooks/useBrand'
import { getRelativeTime } from '@/components/DataFreshnessIndicator'
import type { FreshnessTier } from '@/types/shared'

/**
 * The operator's way to force a STRUCTURE sync — the rooms and rate plans a property has,
 * re-read from the PMS. Deliberately NOT the same thing as a rate recompute: one refreshes the
 * SHAPE of the property, the other recomputes PRICES. Conflating them is what let a property sit
 * two months out of date while its operator kept reaching for Recompute.
 *
 * Two honest constraints the copy must carry, because both bite in practice:
 *   - it is QUEUED, so nothing on screen changes when the button is clicked;
 *   - it is METERED — the PMS allows 6 structure reads an hour PER CONNECTION, shared across
 *     every property on that connection — so it is a deliberate action, not a refresh button.
 *
 * Presentation only. The POST endpoint enforces the :manage_pricing ability and bounces a frozen
 * account with its own flash; this component never tries to pre-empt either.
 */

// Tone per server-decided freshness tier. Thresholds live in SyncHealth — never re-derived here.
// `unknown` (never synced) is amber, matching the WARNING severity SyncHealth gives it.
const TIER_TONE: Record<FreshnessTier, { dot: string; badge: string | null; label: string | null }> = {
  fresh: { dot: 'bg-emerald-500', badge: null, label: null },
  stale: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700', label: 'Out of date' },
  outdated: { dot: 'bg-rose-500', badge: 'bg-rose-100 text-rose-700', label: 'Very out of date' },
  unknown: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700', label: 'Never synced' },
}

interface StructureSyncMetaProps {
  lastSyncedAt: string | null
  freshness: FreshnessTier
  className?: string
}

/**
 * The quiet "where these rooms came from, and how old that is" line. A never-synced property gets
 * its own sentence rather than the string "never" pushed through a relative-time formatter.
 */
export function StructureSyncMeta({ lastSyncedAt, freshness, className }: StructureSyncMetaProps) {
  const tone = TIER_TONE[freshness] ?? TIER_TONE.unknown

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', tone.dot)} aria-hidden="true" />
      <p className="text-xs text-muted-foreground">
        Rooms and rate plans come from your PMS —{' '}
        {lastSyncedAt === null ? (
          <span className="font-medium text-foreground">never synced</span>
        ) : (
          <>
            last synced{' '}
            <span className="font-medium tabular-nums text-foreground">
              {getRelativeTime(lastSyncedAt)}
            </span>
          </>
        )}
      </p>
      {tone.label && (
        <Badge
          variant="outline"
          className={cn('shrink-0 border-transparent text-[11px] font-medium', tone.badge)}
        >
          {tone.label}
        </Badge>
      )}
    </div>
  )
}

interface StructureSyncControlProps {
  /** POST target — `structureSync.syncPath` from the backend, or the property's sync path. */
  syncPath: string
  /** `card` = full-width secondary action in a card footer; `inline` = small standalone action. */
  variant?: 'card' | 'inline'
  label?: string
  className?: string
}

export default function StructureSyncControl({
  syncPath,
  variant = 'inline',
  label = 'Sync structure',
  className,
}: StructureSyncControlProps) {
  const brand = useBrand()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [syncing, setSyncing] = useState(false)

  // A form submission, not navigation — router.post is correct here (and handles CSRF itself).
  // preserveScroll because the endpoint redirects back to the page it was triggered from.
  const handleSync = () => {
    setDialogOpen(false)
    router.post(
      syncPath,
      {},
      {
        preserveScroll: true,
        onStart: () => setSyncing(true),
        onFinish: () => setSyncing(false),
      }
    )
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size={variant === 'card' ? 'default' : 'sm'}
        disabled={syncing}
        aria-busy={syncing}
        onClick={() => setDialogOpen(true)}
        className={cn(
          'rounded-lg border-border/60 text-xs font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground',
          variant === 'card' && 'h-auto w-full px-3 py-2',
          className
        )}
      >
        <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', syncing && 'animate-spin')} />
        {syncing ? 'Queueing...' : label}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Sync rooms &amp; rate plans</DialogTitle>
            <DialogDescription>
              Re-reads your property structure from your PMS, so {brand} knows which rooms and rate
              plans exist and what they are called. Prices are not touched — Recompute does that.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-2 rounded-lg border border-border/60 bg-muted/30 p-3">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Queued, not instant.</span> The refresh
                usually lands within a few minutes — nothing on this page changes until it does.
              </p>
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Metered by your PMS.</span> Only 6
                structure reads an hour are allowed per PMS connection, shared across every property
                on it.
              </p>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="rounded-lg" onClick={handleSync}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Sync structure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
